import React, { useContext } from 'react';
import classes from './CaptionWriter.module.scss';
import formClasses from '../../../components/forms/form.module.scss';
import classNames from 'classnames';
import { Network } from '../../../models/graphql';
import { BoxNotification } from '../../../components/containers/BoxNotification';
import { UseFormReturn } from 'react-hook-form';
import { Select } from '../../../components/forms/Select';
import { CaptionWriterFormValues } from './CaptionWriter.controller';
import { SelectItem } from '../../../components/forms/Select/Select.view';
import { FormElementAction } from '../../../models/basic-objects';
import { Input } from '../../../components/forms/Input';
import { CaptionWriterFooterView } from './CaptionWriter.footer.view';
import { BeaconContext } from '../../../providers/BeaconProvider';
import { CaptionWriterViewType } from '../states/PostEditorGroupDataProvider';

export type CaptionWritterViewProps = {
  //
  networks: Network[];
  aiFormInstance: UseFormReturn<CaptionWriterFormValues>;
  updateBasePrompt: FormElementAction;
  submitHandler: VoidFunction;
  captionResults: string;
  goBack: VoidFunction;
  goToResults: VoidFunction;
  copyText: VoidFunction;
  viewType: CaptionWriterViewType;
  promptLabel: string;
  propmptBase: string;
  loading: boolean;
};

const basePromptItems: SelectItem[] = [
  { label: 'Write a social media post about', value: 'Write a social media post about:\n\n' },
  { label: 'Rewrite the following content', value: 'Rewrite the following content:\n\n' },
  { label: 'Expand the following content', value: 'Expand the following content into a longer piece of text:\n\n' },
  {
    label: 'Summarize the following text',
    value: 'Shorten the following social media post without losing its meaning:\n\n'
  },
  {
    label: 'Continue the following text',
    value:
      'Write a social media post that starts with the text provided, and make sure your response begins with the text:\n\n'
  }
];

export const CaptionWriterView = (props: CaptionWritterViewProps): JSX.Element => {
  const {
    networks,
    aiFormInstance: baseFormInstance,
    updateBasePrompt,
    submitHandler,
    goBack,
    copyText,
    viewType,
    goToResults,
    captionResults,
    promptLabel,
    propmptBase,
    loading
  } = props;
  const aiFormInstance = baseFormInstance as unknown as UseFormReturn;
  const { beacon } = useContext(BeaconContext);

  const hasProfiles = !!networks.length;
  let form: JSX.Element;
  switch (viewType) {
    case CaptionWriterViewType.initial:
      form = (
        <>
          <BoxNotification type={'warning'}>
            <p className={classes.CaptionWriterInfoBoxParagraph}>
              <strong>Welcome to AI Assistant.</strong>{' '}
              {hasProfiles ? (
                <>
                  Say goodbye to writer&apos;s block. Customize our pre-written prompts with your own ideas, and let our
                  AI do the writing for you! <a onClick={() => beacon('article', '669', { type: 'modal' })}>Learn more</a>.
                </>
              ) : (
                <>
                  Please select a profile to start generating captions with AI.{' '}
                  <a onClick={() => beacon('article', '669', { type: 'modal' })}>Learn more</a>.
                </>
              )}
            </p>
          </BoxNotification>
          <div className={classNames('margin-top-small-16')}>
            <Select
              overrideIntent={'mouse'}
              items={basePromptItems}
              name={'propmptBase'}
              rules={{}}
              action={updateBasePrompt}
              size={'large'}
              label={'Choose a prompt base'}
              chooseLabel={'Select one of the prompts'}
              hideDefaultSelectOption={true}
              useFormInstance={aiFormInstance}
              disabled={!hasProfiles}
              readonly={!hasProfiles}
              wrapper={true}
              {...(propmptBase ? { selectedValue: propmptBase } : {})}
            />
            <Input
              label={promptLabel}
              formInstance={aiFormInstance}
              placeholder={
                'Craft your own unique prompt and tell the AI what to write about; or choose from our list of pre-written prompts.'
              }
              name={'prompt'}
              rules={{}}
              size={'large'}
              multiline={true}
              multilineRows={7}
              disabled={!hasProfiles}
              readonly={!hasProfiles}
              inputWrapperClassName={classes.CaptionWriterTextArea}
              wrapper={true}
            />
          </div>
        </>
      );
      break;
    case CaptionWriterViewType.results:
      form = (
        <>
          <div className={formClasses.Label}>Ai Generated Content</div>
          <div className={classNames(formClasses.large, classes.CaptionWriterResults)}>
            {captionResults}
            {loading ? <span className={classes.CaptionWriterBlinkingCursor}></span> : ''}
          </div>
          <div className={'margin-top-small-16 margin-bottom-small-16'}>
            <BoxNotification type={'warning'}>
              <p className={classes.CaptionWriterInfoBoxParagraph}>
                <strong>Disclaimer:</strong> Content generated is not our responsibility. Please review before saving,
                as results may vary in quality and accuracy. <a onClick={() => beacon('article', '669', { type: 'modal' })}>Learn more</a>.
              </p>
            </BoxNotification>
          </div>
        </>
      );
      break;
  }

  return (
    <div className={classes.CaptionWriter}>
      {form}
      <CaptionWriterFooterView
        viewType={viewType}
        submitHandler={submitHandler}
        aiFormInstance={aiFormInstance}
        goBack={goBack}
        goToResults={goToResults}
        copyText={copyText}
        networks={networks}
      />
    </div>
  );
};
